import {PhoneOutlined, VideoCameraOutlined} from '@ant-design/icons';
import {AppointmentGroupAppointment} from '@components/appointment-details/appointment-group-appointment';
import {AppointmentHistory} from '@components/appointment-details/appointment-history';
import {AppointmentNotes} from '@components/appointment-details/appointment-notes';
import {AppointmentTasks} from '@components/appointment-details/appointment-tasks';
import {RequestAddition} from '@components/appointment-request-details/request-addition';
import {Maybe} from '@graphql-tools/utils/typings/types';
import {Alert, Card, Col, Empty, Row, Space, Tooltip} from 'antd';
import Title from 'antd/lib/typography/Title';
import moment from 'moment';
import {useRouter} from 'next/router';
import {FC, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {isDirectBooking} from '../../helpers/appointment-request-config';
import {buildTimestampDateTime, extractTranslation} from '../../helpers/formatters';
import {getAttendeeStatus, timeUnitToGerman, timeUnitToSeconds, useCurrentUser} from '../../helpers/utils';
import {Resource_AppointmentLocation, Resource_AppointmentStatus, useGetAppointmentQuery,} from '@/generated/graphql';
import {AveliosButton} from '../common-layout/avelios-button';
import {AveliosSpinner} from '../loading/avelios-spinner';
import {UserDetails} from '../user/user-details';
import {AppointmentCancellation} from './appointment-cancellation';
import styles from './appointment-details.module.scss';
import {AppointmentHeader} from './appointment-header';
import {AppointmentRebooking} from './appointment-rebooking';
import {CancelAppointmentModal} from './cancel-appointment-modal';
import {FacilityDetails} from './facility-details';

interface AppointmentDetailsCardProps {
  clinicId: Maybe<string> | undefined;
  appointmentId: Maybe<string> | undefined;
  title?: string;
}

export const AppointmentDetails: FC<AppointmentDetailsCardProps> = ({
  appointmentId,
  clinicId,
  title,
}) => {
  const router = useRouter();
  const { formatMessage } = useIntl();

  const { currentUser } = useCurrentUser();

  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);

  const { data: appointmentProxyData, loading } = useGetAppointmentQuery({
    fetchPolicy: 'network-only',
    skip: !appointmentId || !clinicId,
    variables: { appointmentId: appointmentId!, clinicId: clinicId! },
  });
  const appointmentProxy = appointmentProxyData?.getAppointment;

  const portalFacilities = appointmentProxy?.portalFacilityPath?.portalFacilities;

  const appointment = appointmentProxy?.appointment;

  const appointmentStatus = getAttendeeStatus(appointment, currentUser?.id);
  const videoAppointment =
    appointment?.location === Resource_AppointmentLocation.AppointmentLocationVideo;
  const phoneAppointment =
    appointment?.location === Resource_AppointmentLocation.AppointmentLocationPhone;

  const timeOfBooking = appointment?.eventHistory?.find(
    (event) => event?.appointmentStatus === Resource_AppointmentStatus.AppointmentStatusBooked,
  )?.happenedAt;

  const past = moment().isAfter(moment(appointment?.startTime?.seconds * 1000));

  const booked = appointmentStatus === Resource_AppointmentStatus.AppointmentStatusBooked;

  const reserved = appointmentStatus === Resource_AppointmentStatus.AppointmentStatusReserved;
  const cancelled = appointmentStatus === Resource_AppointmentStatus.AppointmentStatusCancelled;

  const appointmentType = appointment?.appointmentType;

  const allowPortalUserCancellation = appointmentType?.allowPortalUserCancellation;
  const appointmentRequestConfig = appointmentType?.appointmentRequestConfig;
  const externalVideoAppointment = appointmentType?.externalVideoAppointment;
  const externalVideoAppointmentNote = appointmentType?.externalVideoAppointmentNote;
  const phoneAppointmentNote = appointmentType?.phoneAppointmentNote;

  const showCancel = allowPortalUserCancellation && (booked || reserved);

  const portalUserCancellationTimeInterval = appointmentType?.portalUserCancellationTimeInterval;
  const portalUserCancellationTimeUnit = appointmentType?.portalUserCancellationTimeUnit;

  const appointmentTime = appointment?.startTime?.seconds;
  const currentTime = Math.floor(Date.now() / 1000);
  const timeDifferenceLimit =
    !!portalUserCancellationTimeInterval && !!portalUserCancellationTimeUnit
      ? portalUserCancellationTimeInterval * timeUnitToSeconds(portalUserCancellationTimeUnit)
      : undefined;

  const difference = appointmentTime - currentTime;

  const cancellationIsNotPossibleOnline = !!timeDifferenceLimit
    ? difference < timeDifferenceLimit
    : false;

  const basicInfos = useMemo(
    () => [...(appointment?.requestBasicInfos ?? [])],
    [appointment?.requestBasicInfos],
  );

  const note = basicInfos.sort(
    (a, b) => b?.submissionDate?.seconds - a?.submissionDate?.seconds,
  )?.[0]?.comment;

  return (
    <AveliosSpinner spinning={loading}>
      {appointmentProxy ? (
        <Space style={{ width: '100%' }} direction={'vertical'} size={8}>
          <AppointmentHeader appointmentProxy={appointmentProxy} title={title} />
          {note && note.length > 0 && <RequestAddition addition={note} />}
          {phoneAppointment && reserved && (
            <Card className={styles.videoAppointmentHelp}>
              <Title>{formatMessage({ id: 'video-appointment' })}</Title>
              <Row style={{ marginTop: 20 }}>
                <Col lg={{ flex: '100px' }} xs={{ flex: 0 }}>
                  <PhoneOutlined className={styles.videoAppointmentIcon} />
                </Col>
                <Col lg={{ flex: 'auto' }} className={styles.videoAppointmentText}>
                  {formatMessage({ id: 'phone-appointment.text' })}
                </Col>
              </Row>
            </Card>
          )}
          {phoneAppointment && booked && (
            <Card>
              <Title level={3} style={{ fontWeight: 'bold' }}>
                {formatMessage({ id: 'phone-appointment' })}
              </Title>
              {!!phoneAppointmentNote && (
                <Alert
                  type={'info'}
                  showIcon
                  message={
                    extractTranslation(phoneAppointmentNote, router.locale) ??
                    formatMessage({
                      id: 'phone-appointment.external.default-text',
                    })
                  }
                />
              )}
            </Card>
          )}
          {videoAppointment && reserved && (
            <Card className={styles.videoAppointmentHelp}>
              <Title>{formatMessage({ id: 'video-appointment' })}</Title>
              <Row style={{ marginTop: 20 }}>
                <Col lg={{ flex: '100px' }} xs={{ flex: 0 }}>
                  <VideoCameraOutlined className={styles.videoAppointmentIcon} />
                </Col>
                <Col lg={{ flex: 'auto' }} className={styles.videoAppointmentText}>
                  {formatMessage({ id: 'video-appointment.text' })}
                </Col>
              </Row>
            </Card>
          )}
          {videoAppointment && booked && (
            <Card>
              <Title level={3} style={{ fontWeight: 'bold' }}>
                {formatMessage({ id: 'video-appointment' })}
              </Title>
              {externalVideoAppointment && (
                <Alert
                  type={'info'}
                  showIcon
                  message={
                    extractTranslation(externalVideoAppointmentNote, router.locale) ??
                    formatMessage({
                      id: 'video-appointment.external.default-text',
                    })
                  }
                />
              )}
              {/*<Card.Meta*/}
              {/*  description={formatMessage({*/}
              {/*    id: 'video-appointment.description',*/}
              {/*  })}*/}
              {/*/>*/}
              {/*<AveliosButton*/}
              {/*  style={{ marginTop: 24 }}*/}
              {/*  block*/}
              {/*  type="primary"*/}
              {/*  icon={<VideoCameraOutlined />}*/}
              {/*  onClick={() => notification.error({ message: 'Feature not implemented' })}*/}
              {/*>*/}
              {/*  {formatMessage({ id: 'video-appointment.start' })}*/}
              {/*</AveliosButton>*/}
            </Card>
          )}
          {clinicId && appointmentId && (
            <AppointmentTasks clinicId={clinicId} parent={{ appointmentId }} />
          )}
          {appointment?.groupAppointment && (
            <AppointmentGroupAppointment appointment={appointment} />
          )}
          <AppointmentCancellation appointment={appointment} />

          {!cancelled && <AppointmentRebooking appointment={appointment} />}

          {appointment?.id && clinicId && (
            <AppointmentHistory
              clinicId={clinicId}
              appointmentId={appointment?.id}
              isDirectBooking={isDirectBooking(appointmentRequestConfig)}
            />
          )}

          {appointmentId && <AppointmentNotes appointmentId={appointmentId} />}

          {/*{booked && (
            <Card>
              <Title level={3} style={{ fontWeight: "bold" }}>
                {formatMessage({ id: "appointment-tasks.title" })}
              </Title>
              <AppointmentTasks
                appointmentId={appointmentId}
                clinicId={clinicId}
              />
            </Card>
          )}*/}
          <Card>
            <FacilityDetails
              portalFacility={
                portalFacilities ? portalFacilities[portalFacilities.length - 1] : undefined
              }
            />
          </Card>
          <Card>
            <UserDetails userOverride={currentUser ?? undefined} reduced />
          </Card>
          {showCancel && (
            <Card>
              <Tooltip
                title={
                  !!appointment?.editingDisabled
                    ? formatMessage({ id: 'appointment-cancel.ishmed-tooltip' })
                    : undefined
                }
              >
                {cancellationIsNotPossibleOnline ? (
                  <Alert
                    message={formatMessage(
                      { id: 'appointment-cannot-be-cancelled-online' },
                      {
                        timeInterval:
                          portalUserCancellationTimeInterval +
                          ' ' +
                          timeUnitToGerman(portalUserCancellationTimeUnit!),
                      },
                    )}
                    type="info"
                    showIcon
                  />
                ) : (
                  <AveliosButton
                    disabled={past || !!appointment?.editingDisabled}
                    block
                    danger
                    onClick={() => setCancelModalOpen(true)}
                  >
                    {formatMessage({ id: 'appointment-cancel' })}
                  </AveliosButton>
                )}

                {!!timeOfBooking?.seconds && (
                  <div className={styles.bookedAt}>
                    {formatMessage(
                      { id: 'appointment.booked-at' },
                      { bookedAt: buildTimestampDateTime(timeOfBooking) },
                    )}
                  </div>
                )}
              </Tooltip>
            </Card>
          )}
        </Space>
      ) : (
        <Card style={{ backgroundColor: 'white' }}>
          <div className={styles.empty}>
            <Empty description={formatMessage({ id: 'appointment-details.empty' })} />
          </div>
        </Card>
      )}

      <CancelAppointmentModal
        appointment={appointment}
        clinicId={clinicId}
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
      />
    </AveliosSpinner>
  );
};
